/**
 * RpaRadioGroup / RpaRadio — codex-styled radio inputs wrapping Mantine's
 * Radio and Radio.Group. The selected option renders with bold label text;
 * all others use normal weight.
 */
import { tokens } from "@blurb/codex-react";
import { createStyles, Radio, RadioGroupProps, RadioProps } from "@mantine/core";
import React from "react";

const useRadioStyles = createStyles({
  root: {
    padding: `${tokens.spacing[1]} 0`,
  },
  body: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
  radio: {
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    borderColor: tokens.color.semantic.border.subtle,
    cursor: "pointer",
    "&:checked": {
      backgroundColor: tokens.color.semantic.bg.primary,
      borderColor: tokens.color.semantic.bg.primary,
    },
  },
  label: {
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.normal,
    lineHeight: tokens.font.lineHeight.normal,
    color: tokens.color.semantic.text.bold,
    paddingLeft: 0,
    cursor: "pointer",
  },
  labelSelected: {
    "font-weight": tokens.font.weight.bold,
  },
});

/* ---------- RpaRadioGroup ---------- */

interface RpaRadioGroupProps extends RadioGroupProps {
  children: React.ReactNode;
}

const RpaRadioGroupContext = React.createContext<string | undefined>(undefined);

export const RpaRadioGroup: React.FC<RpaRadioGroupProps> = ({ children, ...props }) => (
  <RpaRadioGroupContext.Provider value={props.value ?? undefined}>
    <Radio.Group {...props}>{children}</Radio.Group>
  </RpaRadioGroupContext.Provider>
);

/* ---------- RpaRadio ---------- */

type RpaRadioProps = Omit<RadioProps, "classNames">;

export const RpaRadio: React.FC<RpaRadioProps> = ({ value, ...props }) => {
  const { classes, cx } = useRadioStyles();
  const groupValue = React.useContext(RpaRadioGroupContext);
  const isSelected = groupValue === value;

  return (
    <Radio
      value={value}
      classNames={{
        root: classes.root,
        body: classes.body,
        radio: classes.radio,
        label: cx(classes.label, isSelected && classes.labelSelected),
      }}
      {...props}
    />
  );
};
